/**
 * Merging one /sync/pull response into local state (pure). Server contract (src/shared/dto/annotations.ts,
 * src/server/lib/annotations-pull.ts):
 *  - `layers` is the COMPLETE list of layers of the sheet visible to the user (never paged);
 *  - known layers continue as deltas (rows with seq > cursor, tombstones included); unknown and
 *    `reset` layers arrive in FULL (all live rows, no tombstones) in the same response;
 *  - resumed layers (sent in PullRequest.resume) continue a paged full fetch: rows with
 *    seq > resume.after, tombstones included — they are NOT full layers unless listed in `reset`;
 *  - `cursors` says what to store per layer after applying the rows; a visible layer missing from
 *    it (only when `more`) is listed in `resume` (send that back next time) or, from an older
 *    server, must simply be left out of knownLayers next time.
 *
 * Local rules:
 *  - Pulled rows are authoritative, EXCEPT objects with a pending local op: there the newer
 *    (hlcMs, deviceId) wins and a losing pending op is dropped (the server would answer "stale").
 *  - Tombstones are never kept locally: a pulled tombstone removes the local object (a pending
 *    local delete lives in the outbox only).
 *  - A row identical to the local object (same hlcMs, deviceId and layer) is skipped, so the
 *    pull after a push doesn't replace unchanged objects (stable per-page arrays).
 *  - A layer delivered in full first loses its non-pending local objects that are not in this
 *    response (a full fetch carries no tombstones, so anything not in it is gone).
 *  - Layers no longer in `layers` (or listed in `removed`) are deleted locally — unless they were
 *    created on this device and not confirmed yet. Layers deleted locally (delete op pending) are
 *    ignored entirely.
 *  - Cursors only advance from pull responses, never from push responses.
 */
import { hlcCompare, rowToObject, type AnnotationObject } from '../../../shared/annotations';
import { SYNC_LIMITS, type LayerDTO, type PullResponse, type PullResume } from '../../../shared/dto/annotations';

export type PullPayload = PullResponse;

export interface PendingVersion {
  hlcMs: number;
  deviceId: string;
}

export interface PullMergeInput {
  response: PullPayload;
  /** knownLayers sent with this request (layerId → cursor). */
  sent: ReadonlyMap<string, number>;
  /** Layers sent in PullRequest.resume with this request (continuations of a paged full fetch). */
  resumed?: ReadonlySet<string>;
  /** Every layer currently held locally for this sheet. */
  localLayers: Iterable<string>;
  /** Queued (unconfirmed) version of an object, if it has a pending op. */
  pending: (objectId: string) => PendingVersion | undefined;
  /** Current local (live) object, if any. */
  local?: (objectId: string) => AnnotationObject | undefined;
  /** Own layers deleted locally whose delete hasn't reached the server yet. */
  locallyDeleted: ReadonlySet<string>;
  /** Own layers created locally and not yet confirmed by the server. */
  localOnly: ReadonlySet<string>;
}

export interface PullMergeResult {
  /** Server layer metadata to upsert locally. */
  layers: LayerDTO[];
  /** Layers to delete locally (layer, objects, cursor, prefs, pending ops). */
  removedLayers: string[];
  /** Layers delivered in full: clear their non-pending objects (except `keep`) before applying `put`. */
  fullLayers: string[];
  /** Objects of full layers delivered in this response: never cleared. */
  keep: Set<string>;
  /** Objects to write (remote wins). */
  put: AnnotationObject[];
  /** Objects deleted remotely (tombstones that win): remove locally. */
  drop: string[];
  /** Object ids whose pending op lost against a newer remote version. */
  dropPending: string[];
  /** Cursor to store per layer; null = forget it (the layer is pulled in full or resumed next time). */
  cursors: Map<string, number | null>;
  /** Continuations to send back as PullRequest.resume (replaces the previous set). */
  resume: Map<string, PullResume>;
  /** Rows that failed validation (skipped). */
  invalid: number;
  /** Highest hlcMs seen (feed into the local clock). */
  maxHlc: number;
  /** Pull again right away (the response was capped). */
  again: boolean;
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isSeq = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;

const NONE: ReadonlySet<string> = new Set();

export function mergePull(input: PullMergeInput): PullMergeResult {
  const { response, sent, locallyDeleted, localOnly } = input;
  const resumed = input.resumed ?? NONE;
  const respLayers = Array.isArray(response.layers) ? response.layers.filter((l) => !!l && typeof l.id === 'string') : [];
  const respRemoved = Array.isArray(response.removed) ? response.removed : [];
  const respReset = Array.isArray(response.reset) ? response.reset : [];
  const respRows = Array.isArray(response.rows) ? response.rows : [];
  const serverCursors = response.cursors && typeof response.cursors === 'object' ? response.cursors : null;
  const serverResume = response.resume && typeof response.resume === 'object' ? response.resume : null;

  const visible = new Set(respLayers.map((l) => l.id));
  const removed = new Set<string>();
  const remove = (id: string) => {
    if (!localOnly.has(id) && !locallyDeleted.has(id)) removed.add(id);
  };
  for (const id of respRemoved) if (typeof id === 'string' && !visible.has(id)) remove(id);
  for (const id of input.localLayers) if (!visible.has(id)) remove(id);

  const layers = respLayers.filter((l) => !locallyDeleted.has(l.id));
  const accepted = new Set(layers.map((l) => l.id));
  const full = new Set<string>();
  for (const l of layers) if (!sent.has(l.id) && !resumed.has(l.id)) full.add(l.id);
  for (const id of respReset) if (accepted.has(id)) full.add(id);

  // Cursor bookkeeping: the server's values when present (always, for a current server);
  // otherwise max(row.seq) on top of the sent cursor (0 for full layers).
  const cursors = new Map<string, number | null>();
  const fallback = new Map<string, number>();
  for (const l of layers) {
    if (serverCursors) {
      const c = serverCursors[l.id];
      cursors.set(l.id, isNum(c) && c >= 0 ? c : null);
    } else {
      fallback.set(l.id, full.has(l.id) ? 0 : (sent.get(l.id) ?? 0));
    }
  }

  // Continuations: only for accepted layers that got no cursor (kept apart from cursors).
  const resume = new Map<string, PullResume>();
  if (serverResume && response.more === true) {
    for (const l of layers) {
      if (cursors.get(l.id) != null || fallback.has(l.id)) continue;
      const r = serverResume[l.id];
      if (r && isSeq(r.after) && isSeq(r.snapshot)) resume.set(l.id, { after: r.after, snapshot: r.snapshot });
    }
  }

  const put: AnnotationObject[] = [];
  const drop: string[] = [];
  const keep = new Set<string>();
  const dropPending: string[] = [];
  let invalid = 0;
  let maxHlc = 0;
  for (const row of respRows) {
    if (!row || typeof row.layerId !== 'string' || !accepted.has(row.layerId)) continue;
    if (!serverCursors && isNum(row.seq)) fallback.set(row.layerId, Math.max(fallback.get(row.layerId) ?? 0, row.seq));
    const obj = rowToObject(row);
    if (!obj) {
      invalid++;
      continue;
    }
    if (obj.hlcMs > maxHlc) maxHlc = obj.hlcMs;
    const dead = obj.deletedAt !== null;
    if (!dead && full.has(obj.layerId)) keep.add(obj.id);
    const pend = input.pending(obj.id);
    if (pend) {
      if (hlcCompare(obj, pend) > 0) {
        if (dead) drop.push(obj.id);
        else put.push(obj);
        dropPending.push(obj.id);
      }
      continue;
    }
    const cur = input.local?.(obj.id);
    if (dead) {
      if (cur) drop.push(obj.id);
      continue;
    }
    if (cur && cur.hlcMs === obj.hlcMs && cur.deviceId === obj.deviceId && cur.layerId === obj.layerId && cur.deletedAt === null) continue;
    put.push(obj);
  }
  for (const [id, c] of fallback) cursors.set(id, c);

  return {
    layers,
    removedLayers: [...removed],
    fullLayers: [...full],
    keep,
    put,
    drop,
    dropPending,
    cursors,
    resume,
    invalid,
    maxHlc,
    again: response.more === true,
  };
}

/**
 * knownLayers for the next pull: local layers with a stored cursor, except layers not yet created
 * on the server (capped at the server's limit; the rest simply arrive in full).
 */
export function knownLayers(
  cursors: ReadonlyMap<string, number>,
  localOnly: ReadonlySet<string>,
  limit: number = SYNC_LIMITS.knownLayers,
): Record<string, number> {
  const out: Record<string, number> = {};
  let n = 0;
  for (const [id, c] of cursors) {
    if (localOnly.has(id)) continue;
    if (n++ >= limit) break;
    out[id] = c;
  }
  return out;
}

/**
 * PullRequest.resume for the next pull: the stored continuations of layers that are not in
 * knownLayers (known wins on the server too), capped at the server's limit. A layer left out is
 * simply fetched in full again.
 */
export function resumeToSend(
  resume: ReadonlyMap<string, PullResume>,
  known: Readonly<Record<string, number>>,
  localOnly: ReadonlySet<string>,
  limit: number = SYNC_LIMITS.knownLayers,
): Record<string, PullResume> {
  const out: Record<string, PullResume> = {};
  let n = 0;
  for (const [id, r] of resume) {
    if (Object.prototype.hasOwnProperty.call(known, id) || localOnly.has(id)) continue;
    if (n++ >= limit) break;
    out[id] = { after: r.after, snapshot: r.snapshot };
  }
  return out;
}
